/**
 * FolderPickerDialog — select a directory using FS Sidecar.
 *
 * Usage:
 *   <FolderPickerDialog
 *     open={isOpen}
 *     title="Select Folder"
 *     onSelect={(path) => handleSelectFolder(path)}
 *     onCancel={() => setOpen(false)}
 *   />
 */

import type { WsFsEntry } from "@magic-md/engine-client/protocol";
import { useT } from "@magic-md/i18n/react";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { workspaceFs } from "../../lib/engine-fs";
import type { RecentItem } from "../../store/recent-store";
import { DialogButton, DialogOverlay } from "./DialogOverlay";
import { FileBrowser } from "./FileBrowser";

export interface FolderPickerDialogProps {
    open: boolean;
    title?: string;
    onSelect: (folderPath: string) => void;
    onCancel: () => void;
    /** Optional direct-open handler for recent workspace/folder shortcuts. */
    onOpenRecentWorkspace?: (item: RecentItem) => void | Promise<void>;
    /** Initial directory. Defaults to cwd. */
    initialPath?: string;
}

export function FolderPickerDialog({
    open,
    title,
    onSelect,
    onCancel,
    onOpenRecentWorkspace,
    initialPath,
}: FolderPickerDialogProps) {
    const t = useT("editor");
    const [currentPath, setCurrentPath] = useState(initialPath ?? "");
    const [selectedFolder, setSelectedFolder] = useState<string | null>(null);

    const [resolving, setResolving] = useState(false);
    const [openingRecentItem, setOpeningRecentItem] = useState<RecentItem | null>(null);
    const [openRecentError, setOpenRecentError] = useState<string | null>(null);
    const openingRecentRef = useRef(false);
    const busy = openingRecentItem !== null;

    // Resolve initial path
    useEffect(() => {
        if (!open) return;
        setSelectedFolder(null);
        setOpeningRecentItem(null);
        setOpenRecentError(null);
        openingRecentRef.current = false;
        if (initialPath) {
            setCurrentPath(initialPath);
        } else {
            setResolving(true);
            workspaceFs
                .defaultDir()
                .then(setCurrentPath)
                .finally(() => setResolving(false));
        }
    }, [open, initialPath]);

    const handleSelect = useCallback((entry: WsFsEntry) => {
        if (entry.isDirectory) {
            setSelectedFolder(entry.path);
        }
    }, []);

    const handleNavigate = useCallback((path: string) => {
        setCurrentPath(path);
        setSelectedFolder(null);
    }, []);

    const handleOpenRecentWorkspace = useCallback(
        async (item: RecentItem) => {
            if (!onOpenRecentWorkspace || openingRecentRef.current) return;
            openingRecentRef.current = true;
            setOpeningRecentItem(item);
            setOpenRecentError(null);
            try {
                await onOpenRecentWorkspace(item);
                onCancel();
            } catch (err) {
                openingRecentRef.current = false;
                setOpeningRecentItem(null);
                setOpenRecentError(err instanceof Error ? err.message : String(err));
            }
        },
        [onCancel, onOpenRecentWorkspace],
    );

    const handleConfirm = () => {
        if (busy) return;
        // If a subfolder is selected, use it. Otherwise use the current directory.
        onSelect(selectedFolder ?? currentPath);
    };

    return (
        <DialogOverlay
            title={title ?? t("dialog.selectFolder")}
            open={open}
            onClose={onCancel}
            closeDisabled={busy}
            className="w-[820px]"
            footer={
                <>
                    <DialogButton onClick={onCancel} disabled={busy}>
                        {t("common.cancel")}
                    </DialogButton>
                    <DialogButton
                        variant="primary"
                        onClick={handleConfirm}
                        disabled={busy || !currentPath}
                    >
                        {t("dialog.selectFolder")}
                    </DialogButton>
                </>
            }
        >
            {resolving ? (
                <div className="h-[400px] flex items-center justify-center">
                    <Loader2 size={24} className="animate-spin text-muted-foreground" />
                </div>
            ) : !currentPath ? (
                <div className="h-[400px] flex items-center justify-center text-sm text-muted-foreground">
                    {t("dialog.noAccessibleRoots")}
                </div>
            ) : (
                <>
                    <div className="relative h-[420px]" aria-busy={openingRecentItem ? "true" : undefined}>
                        <FileBrowser
                            currentPath={currentPath}
                            onNavigate={handleNavigate}
                            onSelect={handleSelect}
                            onOpenRecentWorkspace={
                                onOpenRecentWorkspace ? handleOpenRecentWorkspace : undefined
                            }
                            mode="folder"
                            selectedPath={selectedFolder ?? undefined}
                        />
                        {openingRecentItem ? (
                            <div
                                className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-background/85 px-6 text-center backdrop-blur-[1px]"
                                role="status"
                                aria-live="polite"
                            >
                                <Loader2 size={22} className="animate-spin text-primary" />
                                <div className="text-sm font-medium text-foreground">
                                    {openingRecentItem.type === "workspace"
                                        ? t("dialog.openingWorkspace")
                                        : t("dialog.openingFolder")}
                                </div>
                                <div className="max-w-[520px] truncate font-mono text-[11px] text-muted-foreground">
                                    {openingRecentItem.path}
                                </div>
                            </div>
                        ) : null}
                    </div>

                    {openRecentError ? (
                        <div className="border-t border-border px-4 py-2 text-xs text-destructive">
                            {t("dialog.openFailed", { message: openRecentError.slice(0, 140) })}
                        </div>
                    ) : null}

                    {/* Selected folder indicator */}
                    {!openRecentError ? (
                        <div className="px-4 py-2 border-t border-border text-xs text-muted-foreground truncate">
                            {selectedFolder ?? currentPath}
                        </div>
                    ) : null}
                </>
            )}
        </DialogOverlay>
    );
}
